import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, Package, QrCode, Clock, Eye, Sparkles, ArrowUpRight } from "lucide-react";

// The Visualiser's KPI tile.
const ACCENT = {
  gold: "text-heda-gold bg-heda-gold/10 border-heda-gold/30",
  olive: "text-heda-olive bg-heda-olive-tint/60 border-heda-olive/30",
  default: "text-heda-ink bg-white border-heda-line",
};
const StatTile = ({ label, value, icon: Icon, tone = "default" }) => (
  <div className="heda-card min-w-0 p-4 md:p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="truncate text-[11px] uppercase tracking-wider text-heda-mute">{label}</span>
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${ACCENT[tone]}`}>
        <Icon size={16} />
      </span>
    </div>
    <div className="mt-2 font-display text-3xl font-bold tabular-nums text-heda-ink">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3 md:mb-8">
        <div className="min-w-0">
          <div className="hed-overline">Dashboard</div>
          <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink md:text-[30px]">
            Good day at the studio.
          </h1>
          <p className="mt-1 max-w-xl text-[13px] text-heda-sub">
            A glance at your catalog, QR codes and AI activity.
          </p>
        </div>
        <Link to="/admin/products/new" className="hed-btn-gold" data-testid="dashboard-new-product-button">
          <Plus size={14} /> New product
        </Link>
      </div>

      {/* Stats */}
      <div className="hed-stagger grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Total Products" value={stats?.total_products ?? "—"} icon={Package} tone="gold" />
        <StatTile label="QR Codes" value={stats?.total_qrs ?? "—"} icon={QrCode} tone="olive" />
        <StatTile label="Awaiting Approval" value={stats?.pending_approval ?? "—"} icon={Clock} />
        <StatTile label="AI Extracting" value={stats?.pending_extraction ?? "—"} icon={Sparkles} />
      </div>

      {/* Most Viewed + Recent */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="heda-card overflow-hidden lg:col-span-2">
          <div className="flex items-center justify-between border-b border-heda-line px-5 py-3">
            <h3 className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
              <Eye size={16} className="text-heda-gold" /> Most viewed
            </h3>
            <span className="hed-overline">By scans</span>
          </div>
          {stats?.most_viewed?.length ? (
            <div>
              {stats.most_viewed.map((p, idx) => (
                <Link
                  to={`/admin/products/${p.id}/edit`}
                  key={p.id}
                  className="flex items-center justify-between gap-3 border-t border-heda-line px-5 py-3 transition first:border-t-0 hover:bg-heda-cream/50"
                  data-testid={`most-viewed-item-${idx}`}
                >
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="w-6 font-display text-xl font-bold tabular-nums text-heda-gold">{idx + 1}</div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-heda-ink">{p.name || "Untitled"}</div>
                      <div className="truncate text-[11.5px] text-heda-mute">{p.category || "—"} · {p.code}</div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 text-sm tabular-nums text-heda-sub">
                    <Eye size={14} /> {p.views || 0}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="px-5 py-8 text-center text-sm text-heda-mute">No views yet. Publish a product to start tracking.</p>
          )}
        </div>

        <div className="heda-card overflow-hidden">
          <div className="flex items-center justify-between border-b border-heda-line px-5 py-3">
            <h3 className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
              <Package size={16} className="text-heda-gold" /> Recent uploads
            </h3>
            <Link to="/admin/products" className="hed-mini-btn" data-testid="view-all-products-link">
              View all
            </Link>
          </div>
          <div className="p-2">
            {(stats?.recent_products || []).slice(0, 5).map((p) => (
              <Link
                key={p.id}
                to={`/admin/products/${p.id}/edit`}
                className="flex items-center gap-3 rounded-xl px-3 py-2 transition hover:bg-heda-cream/50"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-heda-line bg-heda-cream">
                  {p.images?.[0] ? (
                    <img src={fileUrl(p.images[0].storage_path)} className="h-full w-full object-cover" alt="" />
                  ) : (
                    <Package size={16} className="text-heda-olive" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-heda-ink">{p.name || "Untitled draft"}</div>
                  <div className="text-[11.5px] text-heda-mute">
                    {p.status === "published" ? "Published" : "Draft"} · {p.extraction_status}
                  </div>
                </div>
                <ArrowUpRight size={14} className="shrink-0 text-heda-mute" />
              </Link>
            ))}
            {!stats?.recent_products?.length && (
              <p className="px-3 py-6 text-center text-sm text-heda-mute">No uploads yet.</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
